import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { BaseurlService } from '../services/baseurl.service';

// ── Sessions ─────────────────────────────────────────────────────────────────

export interface OutdoorSession {
  id: string;
  label: string;
  date: string;
  hasDogTrack: boolean;
  hasHandheldTrack: boolean;
}

export interface OutdoorSessionDetail {
  id: string;
  label: string;
  tracks: OutdoorTrack[];
}

export interface OutdoorTrack {
  name: string;
  type: 'DOG' | 'HANDHELD';
  points: TrackPoint[];
}

export interface TrackPoint {
  lat: number;
  lon: number;
  time: string; // ISO-8601 instant string from backend
}

// ── Fitness ───────────────────────────────────────────────────────────────────

export interface FitActivity {
  id: string;
  name: string;
  startTime: string | null;
  endTime: string | null;
  durationMinutes: number | null;
  distanceKm: number | null;
  pointCount: number;
}

export interface FitActivityDetail {
  id: string;
  name: string;
  points: TrackPoint[];
}

// ── Notes ─────────────────────────────────────────────────────────────────────

export interface OutdoorNote {
  id: number | null;
  lat: number;
  lon: number;
  title: string;
  body: string | null;
  createdAt: string | null;
}

// ── Photos ────────────────────────────────────────────────────────────────────

export interface Photo {
  uid: string;
  lat: number;
  lon: number;
  takenAt: string | null;
  title: string | null;
  hash: string;
  previewToken: string;
}

export interface OutdoorConfig {
  photoprismUrl: string;
}

export interface PhotoPosition {
  lat: number;
  lng: number;
}

// ── Coverage ──────────────────────────────────────────────────────────────────

export interface CoveragePoint { lat: number; lon: number; }
export interface CoverageTrack { date: string | null; points: CoveragePoint[]; }

// ── Jagdgebiete ───────────────────────────────────────────────────────────────

export interface Jagdgebiet {
  id: number;
  revierId: number | null;
  revierName: string | null;
  typ: string | null;
  stand: number | null;
  flaecheHa: number | null;
  hegerId: number | null;
  hegerName: string | null;
  bescheid: string | null;
  rechtsstand: number | null;
}

// ── Service ───────────────────────────────────────────────────────────────────

@Injectable({ providedIn: 'root' })
export class OutdoorMapService {

  constructor(
    private readonly http: HttpClient,
    private readonly baseurlService: BaseurlService,
  ) {}

  private base(): string {
    return `${this.baseurlService.getBaseUrl()}/outdoor`;
  }

  // Sessions
  listSessions(): Observable<OutdoorSession[]> {
    return this.http.get<OutdoorSession[]>(`${this.base()}/sessions`);
  }

  getSessionDetail(sessionId: string): Observable<OutdoorSessionDetail> {
    return this.http.get<OutdoorSessionDetail>(`${this.base()}/sessions/${encodeURIComponent(sessionId)}`);
  }

  // Fitness
  listFitActivities(): Observable<FitActivity[]> {
    return this.http.get<FitActivity[]>(`${this.base()}/fit`);
  }

  getFitActivityDetail(activityId: string): Observable<FitActivityDetail> {
    return this.http.get<FitActivityDetail>(`${this.base()}/fit/${encodeURIComponent(activityId)}`);
  }

  // Notes
  listNotes(): Observable<OutdoorNote[]> {
    return this.http.get<OutdoorNote[]>(`${this.base()}/notes`);
  }

  createNote(note: OutdoorNote): Observable<OutdoorNote> {
    return this.http.post<OutdoorNote>(`${this.base()}/notes`, note);
  }

  updateNote(id: number, note: OutdoorNote): Observable<OutdoorNote> {
    return this.http.put<OutdoorNote>(`${this.base()}/notes/${id}`, note);
  }

  deleteNote(id: number): Observable<void> {
    return this.http.delete<void>(`${this.base()}/notes/${id}`);
  }

  // Photos
  getConfig(): Observable<OutdoorConfig> {
    return this.http.get<OutdoorConfig>(`${this.base()}/config`);
  }

  listPhotos(): Observable<Photo[]> {
    return this.http.get<Photo[]>(`${this.base()}/photos`);
  }

  photoThumbUrl(hash: string, previewToken: string): string {
    return `${this.base()}/photo-thumb/${hash}?previewToken=${encodeURIComponent(previewToken)}`;
  }

  photoThumbBlobUrl(hash: string, previewToken: string): Observable<string> {
    return this.http.get(this.photoThumbUrl(hash, previewToken), { responseType: 'blob' }).pipe(
      map(blob => URL.createObjectURL(blob)),
    );
  }

  photoFullBlobUrl(hash: string, previewToken: string): Observable<string> {
    const url = `${this.base()}/photo-thumb/${hash}?previewToken=${encodeURIComponent(previewToken)}&size=fit_4096`;
    return this.http.get(url, { responseType: 'blob' }).pipe(
      map(blob => URL.createObjectURL(blob)),
    );
  }

  updatePhotoPosition(uid: string, lat: number, lng: number): Observable<void> {
    return this.http.put<void>(`${this.base()}/photos/${encodeURIComponent(uid)}/position`, { lat, lng });
  }

  // Coverage
  getCoverage(): Observable<CoverageTrack[]> {
    return this.http.get<CoverageTrack[]>(`${this.base()}/coverage`);
  }

  // Jagdgebiete
  listJagdgebiete(): Observable<Jagdgebiet[]> {
    return this.http.get<Jagdgebiet[]>(`${this.base()}/jagdgebiete`);
  }

  getJagdgebietGeom(id: number): Observable<string> {
    return this.http.get(`${this.base()}/jagdgebiete/${id}/geom`, { responseType: 'text' });
  }

  findJagdgebiet(lat: number, lon: number): Observable<number | null> {
    return this.http.get<number | null>(`${this.base()}/jagdgebiete/find`, {
      params: { lat: String(lat), lon: String(lon) },
    });
  }
}
